import Dexie, { type Table } from 'dexie';
import type {
  AppSettings,
  BlobDoc,
  ExportPayload,
  ParsedTrackPayload,
  Playlist,
  Track
} from '../types';

const SETTINGS_ID = 'settings';

class LocalWaveDatabase extends Dexie {
  tracks!: Table<Track, string>;
  blobs!: Table<BlobDoc, string>;
  playlists!: Table<Playlist, string>;
  app!: Table<AppSettings, string>;

  constructor() {
    super('local-wave-db');

    this.version(1).stores({
      tracks: 'id, hash, title, artist, album, audioBlobId, artworkBlobId, createdAt, updatedAt',
      blobs: 'id, type',
      playlists: 'id, name, updatedAt',
      app: 'id'
    });

    this.version(2)
      .stores({
        tracks:
          'id, hash, title, artist, album, audioBlobId, artworkBlobId, fileName, createdAt, updatedAt',
        blobs: 'id, type',
        playlists: 'id, name, updatedAt',
        app: 'id'
      })
      .upgrade(async (tx) => {
        await tx
          .table('tracks')
          .toCollection()
          .modify((track: Partial<Track>) => {
            track.fileName ??= `${track.title ?? 'Track'}.mp3`;
          });
      });
  }

  async ensureDefaults() {
    const existing = await this.app.get(SETTINGS_ID);
    if (!existing) {
      await this.app.put(defaultSettings());
    }
  }

  async getAppSettings() {
    await this.ensureDefaults();
    return (await this.app.get(SETTINGS_ID)) ?? defaultSettings();
  }

  async updateSettings(partial: Partial<AppSettings>) {
    const current = await this.getAppSettings();
    const next = { ...current, ...partial, id: SETTINGS_ID } satisfies AppSettings;
    await this.app.put(next);
    return next;
  }

  async importTrack(payload: ParsedTrackPayload) {
    const duplicate = await this.tracks.where('hash').equals(payload.hash).first();
    if (duplicate) {
      return { duplicate };
    }

    const now = new Date().toISOString();
    const trackId = crypto.randomUUID();
    const audioBlobId = crypto.randomUUID();
    const artworkBlobId = payload.artworkBlob ? crypto.randomUUID() : undefined;

    const track: Track = {
      id: trackId,
      hash: payload.hash,
      title: payload.title,
      artist: payload.artist,
      album: payload.album,
      duration: payload.duration,
      year: payload.year,
      genre: payload.genre,
      artworkBlobId,
      audioBlobId,
      fileName: payload.fileName,
      createdAt: now,
      updatedAt: now
    };

    await this.transaction('rw', this.tracks, this.blobs, async () => {
      await this.blobs.put({ id: audioBlobId, type: 'audio', blob: payload.audioBlob });
      if (artworkBlobId && payload.artworkBlob) {
        await this.blobs.put({ id: artworkBlobId, type: 'artwork', blob: payload.artworkBlob });
      }
      await this.tracks.put(track);
    });

    return { track };
  }

  async deleteTrack(trackId: string) {
    const track = await this.tracks.get(trackId);
    if (!track) return;

    await this.transaction('rw', this.tracks, this.blobs, this.playlists, async () => {
      await this.tracks.delete(trackId);
      await this.blobs.delete(track.audioBlobId);
      if (track.artworkBlobId) {
        await this.blobs.delete(track.artworkBlobId);
      }

      const playlists = await this.playlists.toArray();
      await Promise.all(
        playlists.map((playlist) =>
          this.playlists.put({
            ...playlist,
            trackIds: playlist.trackIds.filter((id) => id !== trackId),
            updatedAt: new Date().toISOString()
          })
        )
      );
    });
  }

  async getBlob(id?: string) {
    if (!id) return undefined;
    return this.blobs.get(id);
  }

  async createPlaylist(name: string) {
    const now = new Date().toISOString();
    const playlist: Playlist = {
      id: crypto.randomUUID(),
      name,
      trackIds: [],
      createdAt: now,
      updatedAt: now
    };
    await this.playlists.put(playlist);
    return playlist;
  }

  async updatePlaylist(playlist: Playlist) {
    await this.playlists.put({ ...playlist, updatedAt: new Date().toISOString() });
  }

  async renamePlaylist(id: string, name: string) {
    const playlist = await this.playlists.get(id);
    if (!playlist) return;
    await this.updatePlaylist({ ...playlist, name });
  }

  async deletePlaylist(id: string) {
    await this.playlists.delete(id);
  }

  async exportLibrary() {
    const [tracks, playlists, appSettings] = await Promise.all([
      this.tracks.toArray(),
      this.playlists.toArray(),
      this.getAppSettings()
    ]);
    const payload: ExportPayload = {
      version: 1,
      exportedAt: new Date().toISOString(),
      tracks,
      playlists,
      appSettings
    };
    return payload;
  }

  async importLibrary(payload: ExportPayload) {
    await this.transaction('rw', this.tracks, this.playlists, this.app, async () => {
      await this.tracks.bulkPut(payload.tracks);
      await this.playlists.bulkPut(payload.playlists);
      await this.app.put(payload.appSettings);
    });
  }
}

export const db = new LocalWaveDatabase();

export function defaultSettings(): AppSettings {
  return {
    id: SETTINGS_ID,
    theme: 'dark',
    repeatMode: 'off',
    shuffleEnabled: false,
    queue: {
      trackIds: [],
      currentIndex: 0
    },
    lastPosition: 0,
    seededDemo: false
  };
}
